import { useEffect, useState } from "react";
import POASteps from "../components/PoaSteps";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePOAStepsStore } from "../hooks/ui/poaStepsDataStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCustomerQueryOptions,
  getTransferPolicyQueryOptions,
  getAdviceQueryOptions,
} from "../hooks/api";
import {
  isFullCredentials,
  useUpdateCustomerData,
} from "../hooks/ui/customerDataStore";
import { SigningComplete } from "../components/signingComplete";
import { detectBankIdRedirect } from "../utils/detectBankIdRedirect";
import { Button, Paper, Text, Title } from "@mantine/core";
import { isMobileDevice } from "../utils/useDeviceDetection";
import {
  formatPoaDate,
  getPoaStatusQueryOptions,
  isPoaBlocked,
} from "../hooks/api/useGetPoaStatus";

const initialHash = window.location.hash;

type SearchParams = {
  from?: string;
};

export const Route = createFileRoute("/poa-renew")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      from: typeof search.from === "string" ? search.from : undefined,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { renewal, setRenewal, setApp } = usePOAStepsStore();
  const queryClient = useQueryClient();
  const { customerData } = useUpdateCustomerData();

  const { from } = Route.useSearch();
  const isMobile = isMobileDevice();

  // Detect new tab opened by BankID redirect, no credentials available
  const [returnedFromBankId] = useState(() =>
    detectBankIdRedirect({
      hash: initialHash,
      nonceKeys: "bankid_poa_sign_nonce",
      cleanupKeys: ["bankid_poa_sign_nonce", "bankid_poa_sign_transaction_id"],
      condition: !isFullCredentials(customerData.credentials),
    }),
  );

  useEffect(() => {
    setRenewal(true);
    setApp(false);
  }, [setRenewal, setApp]);

  // /fmk/store only runs after the BankID signature, so its 409 alone would let a
  // blocked customer sign a POA that is then refused. Checked here, before the steps.
  const poaStatus = useQuery({
    ...getPoaStatusQueryOptions(customerData.credentials),
    enabled: isFullCredentials(customerData.credentials),
  });
  const blockedUntil = poaStatus.data?.blockedUntil;

  const handleCancel = () => {
    navigate({ to: from || "/home" });
  };

  const handleDone = () => {
    const destination = from || "/home";
    if (destination === "/home" && customerData.credentials?.sessionToken) {
      queryClient.prefetchQuery(
        getCustomerQueryOptions(customerData.credentials),
      );
      queryClient.prefetchQuery(
        getTransferPolicyQueryOptions(customerData.credentials),
      );
      queryClient.prefetchQuery(
        getAdviceQueryOptions(customerData.credentials),
      );
    }
    navigate({ to: destination });
  };

  if (returnedFromBankId) {
    return <SigningComplete showCloseButton />;
  }

  return (
    <Paper
      my={isMobile ? "1rem" : "4rem"}
      mx={isMobile ? 0 : "3rem"}
      p={isMobile ? "1rem" : "2rem"}
      radius={"24px"}
    >
      {isPoaBlocked(blockedUntil) ? (
        <>
          <Title
            order={2}
            size="h5"
            mb="1rem"
            ff="var(--wallet-font-family-sambla-sans)"
          >
            Du kan inte ge en ny fullmakt just nu
          </Title>
          <Text mb="2rem">
            Vi hittade ingen pensionsinformation via din fullmakt, så den har
            avslutats. Du kan ge en ny fullmakt från{" "}
            {formatPoaDate(blockedUntil as string)}.
          </Text>
          <Button variant="default" onClick={handleCancel}>
            Tillbaka
          </Button>
        </>
      ) : (
        <POASteps
          setUserCancelled={handleCancel}
          renewal={renewal}
          setHandleDone={handleDone}
        />
      )}
    </Paper>
  );
}
